import { Stack } from "@mui/system"
import { makeStyles } from "tss-react/mui"
import { Box, Typography } from "@mui/material"
import { StandardDialog } from "../../../../../shared/components/dialogs/StandardDialog"
import { LocalizedEnum, LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { MerchantIdCards } from "../idCardsTypes"
import { tailwindPalette } from "../../../../theme/tailwindPalette"
import { StandardChip } from "../../../../../shared/components/badges/StandardChip"
import { useDateTime } from "../../../../localization/useDateTime"

const useStyles = makeStyles()(() => ({
    dialogContainer: {
        padding: "0 1.5rem 1.5rem 1.5rem",
    },
    card: {
        padding: "2.5rem",
        backgroundColor: tailwindPalette.slate[50],
        border: `1px solid ${tailwindPalette.slate[200]}`,
        borderRadius: ".75rem",
    },
}))

export function IdCardsDetailsDialog({
    idCard,
    onClose,
    open,
}: {
    idCard: MerchantIdCards | undefined
    open: boolean
    onClose: () => void
}) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()

    if (!idCard) return <></>

    return (
        <StandardDialog
            dialogTitle={
                <LocalizedStrict id="merchant-id-cards-dialog-title">
                    <>ID card details</>
                </LocalizedStrict>
            }
            subtitle={idCard?.paymentCard?.id}
            open={open}
            onClose={onClose}
            scroll="body"
        >
            <Stack className={classes.dialogContainer} direction="column" spacing="2rem">
                <Stack className={classes.card} direction="column" alignItems="center" spacing=".625rem">
                    <Box display="flex" width="100%" justifyContent="space-between">
                        <LocalizedStrict id="merchant-id-cards-dialog-status">
                            <Typography variant="body1" color="textSecondary">
                                Status
                            </Typography>
                        </LocalizedStrict>
                        {idCard.paymentCard.isActive ? (
                            <LocalizedStrict id="active">
                                <StandardChip variant="success">Active</StandardChip>
                            </LocalizedStrict>
                        ) : (
                            <LocalizedStrict id="inactive">
                                <StandardChip variant="grey">Inactive</StandardChip>
                            </LocalizedStrict>
                        )}
                    </Box>
                    <Box display="flex" width="100%" justifyContent="space-between">
                        <LocalizedStrict id="merchant-id-cards-dialog-card-name">
                            <Typography variant="body1" color="textSecondary">
                                Card name
                            </Typography>
                        </LocalizedStrict>
                        <Typography variant="subtitle1">{idCard.paymentCard.name}</Typography>
                    </Box>
                    <Box display="flex" width="100%" justifyContent="space-between">
                        <LocalizedStrict id="merchant-id-cards-dialog-card-type">
                            <Typography variant="body1" color="textSecondary">
                                Card type
                            </Typography>
                        </LocalizedStrict>
                        <LocalizedEnum
                            base="merchant-id-cards-dialog-card-type"
                            enumValue={idCard.paymentCard.cardType}
                        >
                            <Typography variant="subtitle1">{idCard.paymentCard.cardType}</Typography>
                        </LocalizedEnum>
                    </Box>
                    {idCard.paymentCard?.validated && (
                        <Box display="flex" width="100%" justifyContent="space-between">
                            <LocalizedStrict id="merchant-id-cards-dialog-validated">
                                <Typography variant="body1" color="textSecondary">
                                    Validated:
                                </Typography>
                            </LocalizedStrict>
                            <Typography variant="subtitle1">
                                {dateTimeFactory.formatDate(dateTimeFactory.fromApi(idCard.paymentCard.validated))}
                            </Typography>
                        </Box>
                    )}
                    {idCard.paymentCard?.expiry && (
                        <Box display="flex" width="100%" justifyContent="space-between">
                            <LocalizedStrict id="merchant-id-cards-dialog-expiry">
                                <Typography variant="body1" color="textSecondary">
                                    Expiry:
                                </Typography>
                            </LocalizedStrict>
                            <Typography variant="subtitle1">
                                {dateTimeFactory.formatDate(dateTimeFactory.fromApi(idCard.paymentCard.expiry))}
                            </Typography>
                        </Box>
                    )}
                    {idCard.paymentCard?.paymentMethod && (
                        <Box display="flex" width="100%" justifyContent="space-between">
                            <LocalizedStrict id="merchant-id-cards-dialog-payment-method">
                                <Typography variant="body1" color="textSecondary">
                                    Payment method:
                                </Typography>
                            </LocalizedStrict>
                            <LocalizedEnum base="purchases-payment-method" enumValue={idCard.paymentCard.paymentMethod}>
                                <Typography variant="subtitle1">{idCard.paymentCard.paymentMethod}</Typography>
                            </LocalizedEnum>
                        </Box>
                    )}
                </Stack>
                <Stack className={classes.card} direction="column" alignItems="center" spacing=".625rem">
                    <Box display="flex" width="100%" justifyContent="space-between">
                        <LocalizedStrict id="merchant-id-cards-dialog-user-id">
                            <Typography variant="body1" color="textSecondary">
                                User ID
                            </Typography>
                        </LocalizedStrict>
                        <Typography variant="subtitle1">{idCard.paymentCard.id}</Typography>
                    </Box>
                    <Box display="flex" width="100%" justifyContent="space-between">
                        <LocalizedStrict id="merchant-id-cards-dialog-employee-id">
                            <Typography variant="body1" color="textSecondary">
                                Employee ID
                            </Typography>
                        </LocalizedStrict>
                        <Typography variant="subtitle1">{idCard.user.employeeId}</Typography>
                    </Box>
                    <Box display="flex" width="100%" justifyContent="space-between">
                        <LocalizedStrict id="merchant-id-cards-dialog-name">
                            <Typography variant="body1" color="textSecondary">
                                Name
                            </Typography>
                        </LocalizedStrict>
                        <Typography variant="subtitle1">{idCard.user.displayName}</Typography>
                    </Box>
                    <Box display="flex" width="100%" justifyContent="space-between">
                        <LocalizedStrict id="merchant-id-cards-dialog-email">
                            <Typography variant="body1" color="textSecondary">
                                Email
                            </Typography>
                        </LocalizedStrict>
                        <Typography variant="subtitle1">{idCard.user.email}</Typography>
                    </Box>
                    {idCard.user?.phone && (
                        <Box display="flex" width="100%" justifyContent="space-between">
                            <LocalizedStrict id="merchant-id-cards-dialog-phone">
                                <Typography variant="body1" color="textSecondary">
                                    Phone:
                                </Typography>
                            </LocalizedStrict>
                            <Typography variant="subtitle1">{idCard.user?.phone}</Typography>
                        </Box>
                    )}
                    {idCard.user?.mobile && (
                        <Box display="flex" width="100%" justifyContent="space-between">
                            <LocalizedStrict id="merchant-id-cards-dialog-mobile">
                                <Typography variant="body1" color="textSecondary">
                                    Mobile:
                                </Typography>
                            </LocalizedStrict>
                            <Typography variant="subtitle1">{idCard.user?.mobile}</Typography>
                        </Box>
                    )}
                </Stack>
            </Stack>
        </StandardDialog>
    )
}
